import dayjs from 'dayjs'
import type { PriceSpecification } from 'schema-dts'
import { supplierGetters } from './supplierGetters'
import type {
  EventDate,
  EventDescription,
  GalleryItem,
  MediaItem,
  Price,
  PricingCategory,
  Supplier,
  SupplierInstantBookingSetting,
} from '~/composables/types/event'
import type { PriceItem } from '~/composables/types/algolia'

const getId = (event?: EventDescription): string => {
  return event?.id || ''
}

const getNextInstance = (event: EventDescription): EventDate =>
  event?.next_instance

const getNextDateAvailable = (event?: EventDescription): string | null => {
  return event?.next_instance?.start || null
}

const getPermalink = (event?: EventDescription): string => {
  return event?.permalink || ''
}

const getNextDateAvailableEnd = (event?: EventDescription): string => {
  return event?.next_instance?.end || ''
}

const getNextDateAvailableId = (event?: EventDescription): string => {
  return event?.next_instance?.id || ''
}

const getNextDateAvailableDuration = (
  event?: EventDescription
): string | number | null => {
  return event?.next_instance?.duration || null
}

const getEventMinimumDuration = (event?: EventDescription): number | null => {
  return event?.min_duration || null
}

const getEventMaximumDuration = (event?: EventDescription): number | null => {
  return event?.max_duration || null
}

const getTitle = (event?: EventDescription | undefined): string => {
  return event?.title || ''
}

const getDescription = (event?: EventDescription): string => {
  return event?.description || ''
}

const getShortDescription = (event?: EventDescription): string => {
  return event?.description_summary || ''
}

const getCourseProgress = (event?: EventDescription): string => {
  return event?.course_progress || ''
}

const getCourseTarget = (event?: EventDescription): string => {
  return event?.course_target || ''
}

const getSubtitle = (event?: EventDescription): string => {
  return event?.subtitle || ''
}

const getTags = (event?: EventDescription): Array<any> => {
  return event?.tags?.data || []
}

const getDefaultDuration = (event?: EventDescription): string | number => {
  return event?.default_duration || ''
}

const getWhatIsIncludedSummary = (event?: EventDescription): string => {
  return event?.what_is_included_summary || event?.what_is_included || ''
}

const getTeamEventsAndGiftCardsSummary = (event?: EventDescription): string => {
  return event?.team_events_and_gift_cards_summary || ''
}

const getCategories = (event?: EventDescription) => {
  return event?.categories?.data || event?.categories || ''
}

const getMainCategory = (event?: EventDescription): string | undefined => {
  const categories = event?.categories?.data
  if (categories && categories.length) {
    return categories[0].name || undefined
  }

  return undefined
}

const getMainCategorySlug = (event?: EventDescription): string | undefined => {
  const categories = event?.categories?.data
  if (categories && categories.length) {
    return categories[0].slug
  }

  return undefined
}

const getLocationSummary = (event?: EventDescription): string => {
  return event?.location_summary || ''
}

const getHasMultipleLocations = (event?: EventDescription): boolean => {
  return event?.multiple_locations || false
}

const getWhatToBring = (event?: EventDescription): string => {
  return event?.what_to_bring || ''
}

const getPrivateEventDescription = (event?: EventDescription): string => {
  return event?.private_event_description || ''
}

const getWhatIsIncluded = (event?: EventDescription): string => {
  return event?.what_is_included || ''
}

const getSpecialization = (
  event?: EventDescription
): EventDescription['specialization'] | undefined => {
  return event?.specialization || undefined
}

const getSupplier = (event?: EventDescription): Supplier | undefined => {
  return event?.supplier?.data || undefined
}

const getSupplierLogo = (event?: EventDescription): string => {
  return supplierGetters.getLogo(getSupplier(event))
}

const getSupplierId = (event?: EventDescription) => {
  return getSupplier(event)?.id
}

const getSupplierCreatedAt = (event?: EventDescription): string => {
  return supplierGetters.getCreatedAt(getSupplier(event))
}

const getSupplierDescription = (event?: EventDescription): string => {
  return supplierGetters.getDescription(getSupplier(event))
}

const getSupplierShortDescription = (event?: EventDescription): string => {
  return supplierGetters.getShortDescription(getSupplier(event))
}

const getSupplierName = (event?: EventDescription): string => {
  return supplierGetters.getName(getSupplier(event))
}

const getSupplierUserName = (event?: EventDescription): string => {
  return supplierGetters.getUserName(getSupplier(event))
}

const getSupplierPermalink = (event?: EventDescription): string => {
  return supplierGetters.getPermalink(getSupplier(event))
}

const getSupplierWebsite = (event?: EventDescription): string => {
  return supplierGetters.getWebsite(getSupplier(event))
}

const getEventSupplierSubdomain = (event?: EventDescription): string => {
  return supplierGetters.getSubdomain(getSupplier(event))
}

const getNextInstanceInTimestamp = (
  event?: EventDescription
): number | null => {
  if (!event?.next_instance?.start) {
    return null
  }

  const toTimestamp = new Date(event.next_instance.start).getTime()
  return Math.floor(toTimestamp / 1000) || null
}

const getCoordinates = (event?: EventDescription): any => {
  return event?.address?.data?.coordinate || null
}

const getCoordinatesString = (event?: EventDescription) =>
  getCoordinates(event)?.join(', ')

const getFormattedAddress = (event?: EventDescription): string => {
  return event?.address?.data?.formatted || ''
}

const getAddressZipcode = (event?: EventDescription): string => {
  return event?.address?.data?.zipcode || ''
}

const getAddressLocality = (event?: EventDescription): any => {
  return event?.address?.data?.locality?.data || {}
}

const getAddressLocalityName = (event?: EventDescription): string => {
  return event?.address?.data?.locality?.data.name || ''
}

const getAddressLocalitySlug = (event?: EventDescription): string => {
  return event?.address?.data?.locality?.data.slug || ''
}

const getIsAddressRequired = (event?: EventDescription): boolean => {
  return event.required_address || false
}

const getMinimumPrice = (
  event?: EventDescription,
  from
): string | number | null => {
  return event?.min_price ? from + event?.min_price?.formatted || null : null
}

const getMinimumPriceAmount = (
  event?: EventDescription
): string | number | null => {
  return event?.min_price?.amount || null
}

const getMinimumPriceCurrency = (
  event?: EventDescription
): string | undefined => {
  return event?.min_price?.currency || 'EUR'
}

const getMaximumPrice = (
  event?: EventDescription,
  from
): string | number | null => {
  return event?.max_price ? from + event?.max_price?.formatted || null : null
}

const getMaximumPriceAmount = (
  event?: EventDescription
): string | number | null => {
  return event?.max_price?.amount || null
}

const getMaximumPriceCurrency = (
  event?: EventDescription
): string | number | null => {
  return event?.max_price?.currency || 'EUR'
}

const getPrice = (product): string | number | null => {
  return product?.price?.formatted || null
}

const getPriceAmount = (product): number | null => {
  return product?.price?.amount || product?.data?.price?.amount || null
}

const getTimezone = (event?: EventDescription) => {
  return event?.timezone || null
}

const getDefaultFormattedPrice = (
  event?: EventDescription
): string | number | null => {
  return event?.default_price?.formatted || null
}

const getRetailPriceAmount = (
  event?: EventDescription
): string | number | null => {
  return event?.retail_price?.amount || null
}

const getDefaultPriceAmount = (
  event?: EventDescription
): string | number | null => {
  return event?.default_price?.amount || null
}

const getDefaultPriceObject = (event?: EventDescription): Price | null => {
  return event?.default_price || null
}

const getDefaultPriceCurrency = (
  event?: EventDescription
): string | undefined => {
  return event?.default_price?.currency || 'EUR'
}

const getOriginalPriceObject = (event?: EventDescription): Price | null => {
  return event?.original_price || null
}

const getOriginalPriceCurrency = (
  event?: EventDescription
): string | undefined => {
  return event?.original_price?.currency || 'EUR'
}

const getOriginalPriceFormatted = (
  event?: EventDescription
): string | number | null => {
  return event?.original_price?.formatted || null
}

const getGallery = (event?: EventDescription): GalleryItem[] =>
  event?.media?.map((photo) => ({
    ...photo,
    cropped: replaceSubdomainToProd(photo?.cropped),
    original: replaceSubdomainToProd(photo?.original),
    thumb: replaceSubdomainToProd(photo?.thumb),
  })) || []

const convertWebpToJpg = (url?: string): string => {
  if (!url) return ''
  return url.replace(/\.webp(\?|$)/, '.jpg$1')
}

const getOpenGraphImage = (event?: EventDescription): string => {
  const gallery = getGallery(event)
  if (!gallery || gallery.length === 0 || !gallery[0]) return ''

  const imageUrl = gallery[0].cropped
  return convertWebpToJpg(imageUrl)
}

const getGalleryImages = (event?: EventDescription): GalleryItem[] => {
  const gallery = eventGetters.getGallery(event)
  return gallery?.filter((image) => image?.media_type === 'photo')
}

const getPrincipalImage = (
  event?: EventDescription
): GalleryItem | undefined => {
  const gallery = eventGetters.getGalleryImages(event)
  return gallery?.find((x) => x.isPrincipal)
}

const getFeaturedImages = (event?: EventDescription): GalleryItem[] => {
  const gallery = eventGetters.getGalleryImages(event)
  return gallery?.filter((x) => x.isFeatured) || []
}

const getNonFeaturedImages = (event?: EventDescription): GalleryItem[] => {
  const gallery = eventGetters.getGalleryImages(event)
  return gallery?.filter((x) => !x.isFeatured && !x.isPrincipal) || []
}

const getLandscapeVideo = (event?: EventDescription): GalleryItem | null => {
  const gallery = eventGetters.getGallery(event)
  return (
    gallery?.find(
      (x) => x.media_type === 'video' && x.orientation === 'landscape'
    ) || null
  )
}

const getPortraitVideo = (event?: EventDescription): GalleryItem | null => {
  const gallery = eventGetters.getGallery(event)
  return (
    gallery?.find(
      (x) => x.media_type === 'video' && x.orientation === 'portrait'
    ) || null
  )
}

const getFirstImageAlt = (event?: EventDescription): string => {
  const media = event?.media
  if (!media || media.length === 0) return ''

  // Find first photo media instead of just first media
  const firstPhoto = media.find((item) => item.media_type === 'photo')
  if (firstPhoto) {
    return firstPhoto.alt
  }

  // Fallback to first media if no photo found
  return media[0]?.alt || ''
}

const getFirstThumb = (event?: EventDescription): string | undefined => {
  const media = event?.media
  if (!media || media.length === 0) return undefined

  // Find first photo media instead of just first media
  const firstPhoto = media.find((item) => item.media_type === 'photo')
  if (firstPhoto) {
    return replaceSubdomainToProd(firstPhoto.thumb)
  }

  // Fallback to first media if no photo found
  return replaceSubdomainToProd(media[0]?.thumb)
}

const getAttendanceMode = (
  event?: EventDescription
): 'HYBRID' | 'IN_PERSON' | 'ONLINE' | 'NATIONWIDE' => {
  return event?.type || ''
}

const hasShippingCosts = (event: EventDescription): boolean =>
  event?.has_shipping_costs > 0

const getIsTeamEvent = (event?: EventDescription | null): boolean => {
  return Boolean(event?.is_team_event) || false
}

const getContentLocale = (event?: EventDescription): string => {
  return event?.content_locale || ''
}

const getMetaDescription = (event?: EventDescription): string => {
  return event?.meta_description || ''
}
const getLanguages = (event?: EventDescription): string[] | null => {
  return event?.languages || null
}

const getHasMobileClasses = (event?: EventDescription): boolean | undefined => {
  return event?.has_mobile_classes
}

const getReviewsAverage = (
  event?: EventDescription,
  locale = 'de-DE'
): string | null => {
  const avg = getReviewsRating(event)

  if (avg) {
    return new Intl.NumberFormat(locale, {
      maximumSignificantDigits: 2,
      minimumSignificantDigits: 2,
    }).format(avg)
  }

  return null
}

const getReviewsRating = (event?: EventDescription): number | null => {
  return event?.avg_review || event?.supplier?.data?.avg_review || null
}

const isGiftcardEvent = (event?: EventDescription): boolean =>
  event?.supplier?.data?.name === 'konfetti Gutscheine'

const isBoxEvent = (event?: EventDescription): boolean =>
  event?.specialization === 'KIT'

const getIsPartnerRating = (event?: EventDescription): boolean => {
  if (isGiftcardEvent(event)) {
    return false
  }
  const eventRating = event?.avg_review
  const partnerRating = event?.supplier?.data?.avg_review
  // It's a partner rating if event rating is null/undefined/0 but there is a partner rating > 0
  return (eventRating == null || eventRating === 0) && partnerRating > 0
}

const getReviewsTotal = (event?: EventDescription): number => {
  return event?.total_reviews || 0
}

const getReviewsList = (event?: EventDescription): any[] => {
  return event?.reviews?.data || []
}

const getReviewBreakdown = (event?: EventDescription) => {
  return event?.review_breakdown ?? null
}

// Reviews list sorted by Recency first (created_at decreasing)
const getReviewsListRecentFirst = (event?: EventDescription): any[] => {
  return getReviewsList(event)
    .slice()
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
}

const getAllReviewMedia = (event?: EventDescription): MediaItem[] => {
  const reviews = getReviewsList(event) || []
  return reviews
    .flatMap((review) => review.media?.data || [])
    .filter(
      (media) => media.mediaType === 'photo' || media.mediaType === 'video'
    )
}

const getAllReviewMediaWithDates = (
  event?: EventDescription
): Array<{ media: MediaItem; reviewDate: string }> => {
  const reviews = getReviewsList(event) || []
  return reviews.flatMap((review) =>
    (review.media?.data || [])
      .filter(
        (media) => media.mediaType === 'photo' || media.mediaType === 'video'
      )
      .map((media) => ({
        media,
        reviewDate: review.created_at,
      }))
  )
}

const getAllReviewMediaWithReviewInfo = (
  event?: EventDescription
): Array<{
  media: MediaItem
  reviewDate: string
  customer: string
  rating: number
  reviewId: string
}> => {
  const reviews = getReviewsList(event) || []
  return reviews.flatMap((review) =>
    (review.media?.data || [])
      .filter(
        (media) => media.mediaType === 'photo' || media.mediaType === 'video'
      )
      .map((media) => ({
        media,
        reviewDate: review.created_at,
        customer: review.customer || '',
        rating: review.rating ?? 0,
        reviewId: String(review.id),
      }))
  )
}

const transformMediaItemToGalleryItem = (mediaItem: MediaItem): GalleryItem => {
  return {
    id: mediaItem.id,
    filename: mediaItem.file_name || '',
    original: mediaItem.cdn_url || mediaItem.url || '',
    thumb: mediaItem.thumb || mediaItem.cdn_url || mediaItem.url || '',
    cropped:
      mediaItem.crop?.cropImg ||
      mediaItem.thumb ||
      mediaItem.cdn_url ||
      mediaItem.url ||
      '',
    dimensions: {
      width: 0,
      height: 0,
    },
    social_cropped: null,
    media_type: mediaItem.mediaType as 'photo' | 'video',
    orientation: (mediaItem.orientation || 'square') as
      | 'landscape'
      | 'portrait'
      | 'square',
    isPrincipal: mediaItem.principal || false,
    isFeatured: mediaItem.featured || false,
    streaming_url: mediaItem.streaming_url,
    alt: mediaItem.alt || '',
    src:
      mediaItem.crop?.cropImg ||
      mediaItem.thumb ||
      mediaItem.cdn_url ||
      mediaItem.url ||
      '',
  }
}

const transformMediaItemsToGalleryItems = (
  mediaItems: MediaItem[]
): GalleryItem[] => {
  return mediaItems.map(transformMediaItemToGalleryItem)
}

const getIsApproved = (event?: EventDescription): boolean => {
  return event?.approved || false
}

const getStatus = (event?: EventDescription): string => {
  return event?.status || ''
}

const getEventIdFromPath = (path: string): string =>
  path?.split('-').pop() || ''

const truthySettingFlag = (value: unknown): boolean => {
  if (value === true || value === 1 || value === '1') {
    return true
  }
  if (typeof value === 'string' && value.toLowerCase() === 'true') {
    return true
  }
  return false
}

const getDateRequestSettingsData = (event?: EventDescription) => {
  if (!event) {
    return undefined
  }
  return (
    event.dateRequestSettings?.data ??
    event.date_request_settings?.data ??
    undefined
  )
}

const getPrivateEventSettingsData = (event?: EventDescription) => {
  if (!event) {
    return undefined
  }
  return (
    event.privateEventSettings?.data ??
    event.private_event_settings?.data ??
    undefined
  )
}

/** When the API does not supply active private-event settings. */
const DEFAULT_PRIVATE_EVENT_MIN_TICKETS = 11

/** Private-event requests allowed when settings are missing (legacy) or `active` is true. */
const isPrivateEventRequestEnabled = (event?: EventDescription): boolean => {
  const s = getPrivateEventSettingsData(event)
  if (s === undefined) {
    return true
  }
  return truthySettingFlag(s.active)
}

const getPrivateEventMinTicketsThreshold = (
  event?: EventDescription
): number => {
  const s = getPrivateEventSettingsData(event)
  if (
    s !== undefined &&
    truthySettingFlag(s.active) &&
    typeof s.min_tickets === 'number' &&
    s.min_tickets > 0
  ) {
    return s.min_tickets
  }
  return DEFAULT_PRIVATE_EVENT_MIN_TICKETS
}

type InstantBookingSettingsBlock =
  | { data?: SupplierInstantBookingSetting | null }
  | SupplierInstantBookingSetting
  | undefined

const unwrapInstantBookingSettings = (
  block: InstantBookingSettingsBlock
): SupplierInstantBookingSetting | undefined => {
  if (!block || typeof block !== 'object') {
    return undefined
  }
  if ('data' in block) {
    const nested = block.data
    if (nested !== undefined && nested !== null) {
      return nested
    }
    return undefined
  }
  if ('active' in block) {
    return block as SupplierInstantBookingSetting
  }
  return undefined
}

const getInstantBookingSettingsData = (event?: EventDescription) => {
  if (!event) {
    return undefined
  }
  return (
    unwrapInstantBookingSettings(
      event.instantBookingSettings as InstantBookingSettingsBlock
    ) ??
    unwrapInstantBookingSettings(
      event.instant_booking_settings as InstantBookingSettingsBlock
    )
  )
}

const isDateRequestActive = (event?: EventDescription): boolean => {
  const s = getDateRequestSettingsData(event)
  if (s !== undefined) {
    return truthySettingFlag(s.active)
  }
  return event?.request_active === 1 || event?.request_active === true || false
}

const isDateRequestBookingSolution = (event?: EventDescription): boolean => {
  const s = getDateRequestSettingsData(event)
  if (s !== undefined) {
    return truthySettingFlag(s.booking_solution)
  }
  return (
    event?.request_booking_solution === 1 ||
    event?.request_booking_solution === true ||
    false
  )
}

/* isDateRequest Active on Booking solution */
const isDateRequestActiveBS = (
  event?: EventDescription,
  isSubdomain = false
): boolean =>
  isSubdomain
    ? isDateRequestActive(event) && isDateRequestBookingSolution(event)
    : isDateRequestActive(event)

/** Date-request or private-event request CTA on buy-box / calendar. */
const isDateOrPrivateEventRequestEnabled = (
  event?: EventDescription,
  isSubdomain = false
): boolean =>
  isDateRequestActiveBS(event, isSubdomain) ||
  isPrivateEventRequestEnabled(event)

const isPrivateDateRequestActive = (event?: EventDescription): boolean => {
  const s = getDateRequestSettingsData(event)
  if (s !== undefined) {
    return truthySettingFlag(s.private)
  }
  return event?.request_private === 1 || false
}

const isDateRequestOrWaitingActive = (
  event?: EventDescription,
  isSubdomain = false
): boolean => isDateRequestActiveBS(event, isSubdomain) || true

const isDateRequestActiveWithSubdomainCheck = (
  event?: EventDescription,
  isSubdomain = false
): boolean => isDateRequestActiveBS(event, isSubdomain) || true

const getDateRequestMinAdvanceQuantity = (event?: EventDescription): number => {
  const s = getDateRequestSettingsData(event)
  if (s !== undefined && typeof s.min_advance === 'number') {
    return s.min_advance
  }
  return event?.request_min_advance ?? 0
}

const getDateRequestMinAdvanceUnit = (event?: EventDescription): string => {
  const s = getDateRequestSettingsData(event)
  if (s !== undefined && s.min_advance_type !== undefined) {
    return s.min_advance_type
  }
  return event?.request_min_advance_type ?? ''
}

const getMinDateRequestTickets = (event?: EventDescription): number => {
  const s = getDateRequestSettingsData(event)
  if (s !== undefined && typeof s.min_tickets === 'number') {
    return s.min_tickets
  }
  return event?.request_min_tickets ?? 1
}

const getMaxDateRequestTickets = (event?: EventDescription): number => {
  const s = getDateRequestSettingsData(event)
  if (s !== undefined && typeof s.max_tickets === 'number') {
    return s.max_tickets
  }
  return event?.request_max_tickets ?? 50
}

const getDateRequestAvailableDays = (event?: EventDescription): number[] => {
  const s = getDateRequestSettingsData(event)
  if (s !== undefined && Array.isArray(s.week_days)) {
    return s.week_days
  }
  return event?.request_days ?? []
}

const isInstantBookingActive = (event?: EventDescription): boolean => {
  const s = getInstantBookingSettingsData(event)
  if (s === undefined) {
    return false
  }
  return truthySettingFlag(s.active)
}

const isInstantBookingBookingSolution = (event?: EventDescription): boolean => {
  const s = getInstantBookingSettingsData(event)
  if (s === undefined) {
    return false
  }
  return truthySettingFlag(s.booking_solution)
}

const isInstantBookingPrivate = (event?: EventDescription): boolean => {
  const s = getInstantBookingSettingsData(event)
  if (s === undefined) {
    return false
  }
  return truthySettingFlag(s.private)
}

const getInstantBookingMinAdvanceQuantity = (
  event?: EventDescription
): number => {
  const s = getInstantBookingSettingsData(event)
  return typeof s?.min_advance === 'number' ? s.min_advance : 0
}

const getInstantBookingMinAdvanceUnit = (event?: EventDescription): string => {
  const s = getInstantBookingSettingsData(event)
  return s?.min_advance_type ?? ''
}

const parseSettingNumber = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    if (Number.isFinite(n)) {
      return n
    }
  }
  return fallback
}

const getMinInstantBookingTickets = (event?: EventDescription): number => {
  const s = getInstantBookingSettingsData(event)
  return parseSettingNumber(s?.min_tickets, 1)
}

const getMaxInstantBookingTickets = (event?: EventDescription): number => {
  const s = getInstantBookingSettingsData(event)
  return parseSettingNumber(s?.max_tickets, 50)
}

const getInstantBookingWeekDays = (event?: EventDescription): number[] => {
  const s = getInstantBookingSettingsData(event)
  return Array.isArray(s?.week_days) ? s.week_days : []
}

const getMaxDate = (event?: EventDescription): string => {
  return event?.max_date
}

const getTotalAvailableDates = (event?: EventDescription): number => {
  return event?.total_future_dates || 0
}

const getNeedsMajority = (event: EventDescription): boolean =>
  event?.needs_majority ?? false

const getHasPromotion = (event: EventDescription): boolean => {
  return event?.has_promotions
}

const getRegularPrice = (item: EventDescription): PriceItem | undefined => {
  return item?.original_price || item?.default_price || 0
}

const getSpecialPrice = (item: EventDescription): PriceItem | undefined => {
  return getHasPromotion(item) ? item?.default_price : undefined
}

const getRegularPriceAmount = (item: EventDescription): number | undefined => {
  if (!item) return undefined
  return parseInt(getRegularPrice(item)?.amount || '0')
}

const getSpecialPriceAmount = (item: EventDescription): number | undefined => {
  if (!item) return undefined
  return parseInt(getSpecialPrice(item)?.amount || '0')
}

const getPromoDaysLeft = (item?: EventDescription): number | undefined => {
  return item?.activePromotion?.data?.days_left
}

const getPromoEndDate = (
  item?: EventDescription,
  format?: string
): string | undefined => {
  if (format)
    return dayjs(item?.activePromotion?.data?.period_ends_at)
      .tz('Europe/Berlin')
      .format(format)
  return item?.activePromotion?.data?.period_ends_at || undefined
}

const getOriginalPrice = (item?: EventDescription): Price | undefined => {
  return item?.original_price
}
const getOriginalPriceAmount = (item: EventDescription): number | undefined => {
  if (!item) return undefined
  return parseInt(getOriginalPrice(item)?.amount || '0')
}

const getSchemaPriceSpecification = (
  item: EventDescription
): PriceSpecification[] =>
  eventGetters.getHasPromotion(item)
    ? eventGetters.getSchemaPromotionPrices(item)
    : [eventGetters.getSchemaBasePrice(item)]

const getSchemaPromotionPrices = (item: EventDescription): any => [
  {
    '@type': 'PriceSpecification',
    price: getOriginalPriceAmount(item) || 0 / 100,
    priceCurrency: eventGetters.getDefaultPriceCurrency(item),
  } as PriceSpecification,
  {
    '@type': 'PriceSpecification',
    price: (getSpecialPriceAmount(item) || 0) / 100,
    priceCurrency: eventGetters.getDefaultPriceCurrency(item),
    validThrough: eventGetters.getPromoEndDate(item, 'YYYY-MM-DD'),
  } as PriceSpecification,
]

const getSchemaBasePrice = (item: EventDescription): PriceSpecification => ({
  '@type': 'PriceSpecification',
  price: (Number(getDefaultPriceAmount(item)) || 0) / 100,
  priceCurrency: getDefaultPriceCurrency(item),
})

const categoryHasTieredPricing = (
  category?: PricingCategory | { pricingScales?: { data?: unknown[] } }
): boolean => {
  if (!category?.pricingScales?.data) return false
  return category.pricingScales.data.length > 1
}

const isGroupPricing = (event?: EventDescription): boolean => {
  return event?.is_group_pricing ?? false
}

const getGroupPricingMaxParticipants = (
  event?: EventDescription
): number | undefined => {
  const scales = event?.pricing?.data?.pricingCategories?.data?.find(
    (cat) => cat.type === 'group'
  )?.pricingScales?.data

  if (!scales?.length) return undefined
  return scales[scales.length - 1]?.max_participants
}

const getGroupPricingFirstScaleMaxParticipants = (
  event?: EventDescription
): number | undefined => {
  const scales = event?.pricing?.data?.pricingCategories?.data?.find(
    (cat) => cat.type === 'group'
  )?.pricingScales?.data
  if (!scales?.length) return undefined
  return scales[0]?.max_participants
}

/**
 * Returns the max_participants of the pricing scale that contains the given quantity.
 * Matches the scale where min_participants <= quantity <= max_participants.
 */
const getGroupPricingMaxParticipantsForQuantity = (
  event?: EventDescription,
  quantity?: number
): number | undefined => {
  const scales = event?.pricing?.data?.pricingCategories?.data?.find(
    (cat) => cat.type === 'group'
  )?.pricingScales?.data
  if (!scales?.length || quantity == null) return undefined

  const matchingScale = scales.find(
    (scale) =>
      quantity >= scale.min_participants && quantity <= scale.max_participants
  )
  return matchingScale?.max_participants
}

const hasTieredPricing = (event?: EventDescription): boolean => {
  const categories = event?.pricing?.data?.pricingCategories?.data || []

  // Check if any category has multiple pricing scales (tiers)
  return (
    !isGroupPricing(event) &&
    categories.some((cat) => categoryHasTieredPricing(cat))
  )
}

export const eventGetters = {
  getId,
  getTitle,
  getDescription,
  getShortDescription,
  getSubtitle,
  getTags,
  getEventIdFromPath,
  getIsAddressRequired,
  getDefaultDuration,
  getWhatIsIncludedSummary,
  getCategories,
  getMainCategory,
  getMainCategorySlug,
  getTeamEventsAndGiftCardsSummary,
  getLocationSummary,
  getHasMultipleLocations,
  getWhatToBring,
  getPrivateEventDescription,
  getWhatIsIncluded,
  getSpecialization,
  getSupplierCreatedAt,
  getSupplierDescription,
  getSupplierShortDescription,
  getCourseProgress,
  getCourseTarget,
  getSupplierName,
  getSupplierWebsite,
  getEventSupplierSubdomain,
  getNextInstanceInTimestamp,
  getCoordinates,
  getCoordinatesString,
  getFormattedAddress,
  getAddressZipcode,
  getAddressLocality,
  getAddressLocalityName,
  getAddressLocalitySlug,
  getMinimumPrice,
  getMinimumPriceAmount,
  getMinimumPriceCurrency,
  getMaximumPrice,
  getMaximumPriceAmount,
  getMaximumPriceCurrency,
  getGallery,
  getGalleryImages,
  getOpenGraphImage,
  getPrincipalImage,
  getFeaturedImages,
  getLandscapeVideo,
  getPortraitVideo,
  getPrice,
  getNextInstance,
  getNextDateAvailable,
  getNextDateAvailableEnd,
  getNextDateAvailableDuration,
  getNextDateAvailableId,
  getEventMinimumDuration,
  getFirstImageAlt,
  getFirstThumb,
  getPriceAmount,
  getAttendanceMode,
  getSupplierLogo,
  getSupplierId,
  getPermalink,
  getIsTeamEvent,
  getContentLocale,
  getDefaultFormattedPrice,
  getMetaDescription,
  getTimezone,
  getEventMaximumDuration,
  getDefaultPriceAmount,
  getDefaultPriceObject,
  getDefaultPriceCurrency,
  getOriginalPriceObject,
  getOriginalPriceCurrency,
  getOriginalPriceFormatted,
  getLanguages,
  getSupplier,
  getReviewsAverage,
  getReviewsRating,
  getReviewsTotal,
  getReviewsList,
  getReviewBreakdown,
  getReviewsListRecentFirst,
  getAllReviewMedia,
  getAllReviewMediaWithDates,
  getAllReviewMediaWithReviewInfo,
  transformMediaItemToGalleryItem,
  transformMediaItemsToGalleryItems,
  getIsApproved,
  getStatus,
  getSupplierUserName,
  getSupplierPermalink,
  getNonFeaturedImages,
  isGiftcardEvent,
  isBoxEvent,
  getIsPartnerRating,
  isDateRequestActive,
  isDateRequestBookingSolution,
  isDateRequestOrWaitingActive,
  isDateRequestActiveWithSubdomainCheck,
  isDateRequestActiveBS,
  isDateOrPrivateEventRequestEnabled,
  isPrivateDateRequestActive,
  hasShippingCosts,
  getMaxDateRequestTickets,
  getMinDateRequestTickets,
  isPrivateEventRequestEnabled,
  getPrivateEventMinTicketsThreshold,
  getDateRequestAvailableDays,
  isInstantBookingActive,
  isInstantBookingBookingSolution,
  isInstantBookingPrivate,
  getInstantBookingMinAdvanceQuantity,
  getInstantBookingMinAdvanceUnit,
  getMinInstantBookingTickets,
  getMaxInstantBookingTickets,
  getInstantBookingWeekDays,
  getNeedsMajority,
  getDateRequestMinAdvanceQuantity,
  getDateRequestMinAdvanceUnit,
  getHasMobileClasses,
  getMaxDate,
  getTotalAvailableDates,
  getRegularPrice,
  getSpecialPrice,
  getRegularPriceAmount,
  getSpecialPriceAmount,
  getPromoDaysLeft,
  getPromoEndDate,
  getHasPromotion,
  getSchemaPriceSpecification,
  getSchemaPromotionPrices,
  getSchemaBasePrice,
  getRetailPriceAmount,
  hasTieredPricing,
  categoryHasTieredPricing,
  isGroupPricing,
  getGroupPricingMaxParticipants,
  getGroupPricingMaxParticipantsForQuantity,
  getGroupPricingFirstScaleMaxParticipants,
}
